import React, { Component } from 'react';
import { distance, center, point, featureCollection } from '@turf/turf';
import loadCSV from './loadCSV';
import loadTSV from './loadTSV';
import Entry from './Entry';
import { parseCompanies } from './constants';
import SearchIcon from '../../../shared/icons/Search';
import TextField from '@material-ui/core/TextField';

import Wrap from '../sections/Wrap';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';

const COMPANY_DATA = '/data.csv';
const ZIP_DATA = '/plz-tab.tab';

const buttonDefinitions = [
  ['Pelletfachbetriebe', 'Pelletfachbetrieb'],
  ['Ofenstudios', 'Pelletöfen'],
].map(([label, key]) => ({ label, key }));

const extractTag = (data, tag) => {
  if (data[tag] === 'x') return tag;
  return null;
};

const useStyles = makeStyles(theme => ({
  searchInput: {
    borderLeftStyle: 'solid',
    borderLeftWidth: 1,
    borderLeftColor: theme.palette.grey[600],
    borderRightStyle: 'solid',
    borderRightWidth: 1,
    borderRightColor: theme.palette.grey[600],
    padding: theme.spacing(1.5),
    margin: theme.spacing(1.5, 0),
    minWidth: 250,
    width: '100%',
  },
  input: {
    fontSize: 20,
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  adornment: {
    height: '100%',
  },
  buttonContainer: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey[600],
    paddingBottom: theme.spacing(2),
    '& > section': {
      '& > *': {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
      },
    },
  },
}));

const SearchInput = props => {
  const { onSubmit, ...rest } = props;
  const classes = useStyles();
  return (
    <TextField
      className={classes.searchInput}
      label=""
      placeholder="Suche"
      InputProps={{
        className: classes.input,
        disableUnderline: true,
        endAdornment: (
          <InputAdornment position="end">
            <SearchIcon className={classes.searchIcon} onClick={onSubmit} />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  );
};

const ButtonContainer = props => {
  const classes = useStyles();
  return (
    <Wrap
      noPadding
      display="flex"
      justifyContent="center"
      className={classes.buttonContainer}
      {...props}
    />
  );
};

class PLZSearch extends Component {
  // eslint-disable-line
  state = {
    companies: null,
    zips: null,
    query: null,
    filter: null,
  };
  componentDidMount() {
    loadTSV(ZIP_DATA)
      .then(r => {
        const zips = r.reduce((acc, val) => {
          acc[val.plz] = {
            ...val,
            point: point([parseFloat(val.lon), parseFloat(val.lat)]),
          };
          return acc;
        }, {});
        loadCSV(COMPANY_DATA)
          .then(r => {
            this.setState(prevState => ({
              ...prevState,
              companies: r
                .map(c => parseCompanies(c, zips))
                .filter(c => c.location),
              zips,
            }));
          })
          .catch(e => {
            console.warn('error loading data for PLZSearch:', COMPANY_DATA);
            console.error(e);
          });
      })
      .catch(e => {
        console.warn('error loading data for PLZSearch:', ZIP_DATA);
        console.error(e);
      });
  }
  handleSearch = e => {
    this.setState({
      query: e.target.value,
    });
  };
  get filter() {
    return this.state.filter;
  }
  get query() {
    if (!this.state.query || this.state.query.length < 3) {
      return null;
    }
    return this.state.query;
  }
  get queryLocation() {
    if (!this.query) return null;
    const regex = new RegExp(`^${this.query}.*`, 'gi');
    const matchedZip = Object.keys(this.state.zips).filter(
      key => key.match(regex) !== null,
    );
    const matchedZipPoints = matchedZip.map(key => this.state.zips[key].point);
    const features = featureCollection(matchedZipPoints);
    if (features.features.length === 0) return null;
    return center(features);
  }
  get data() {
    const queryLocation = this.queryLocation;
    if (!queryLocation) return [];
    const regex = new RegExp(`^${this.query}.+`, 'gi');
    return this.state.companies
      .map(v => {
        const dist = distance(v.location, queryLocation.geometry.coordinates);
        const match = v['PLZ'].match(regex);
        return {
          ...v,
          distance: dist,
          zipMatch: match,
        };
      })
      .filter(v => v.distance < 150)
      .sort((a, b) => {
        // direkt zip matches come first
        if (a.zipMatch) return -1;
        if (b.zipMatch) return 1;
        // distance second
        if (a.distance > b.distance) return 1;
        if (a.distance < b.distance) return -1;
        return 0;
      })
      .filter(v => (!this.filter ? true : v[this.filter] === 'x'));
  }
  renderResults = () => {
    if (!this.query) {
      return (
        <React.Fragment>
          <Typography variant="body1" align="center">
            Finden Sie Pelletfachbetriebe und Ofenstudios ganz in Ihrer Nähe.
          </Typography>
          <Typography variant="body1" align="center">
            Geben Sie Ihre Postleitzahl im Suchfeld oben ein.
          </Typography>
        </React.Fragment>
      );
    }
    if (this.query && this.data.length === 0) {
      return (
        <Typography variant="body1" align="center">
          Ihre Suchanfrage hat leider kein Ergebnis erziehlt. Wählen Sie bitte
          eine andere PLZ.
        </Typography>
      );
    }

    return (
      <React.Fragment>
        <Box>
          <Typography variant="body1">
            Es wurden <b>{this.data.length} Treffer</b> in der Nähe der
            gewählten Postleitzahl gefunden:
          </Typography>
        </Box>
        {this.data.map((data, i) => {
          return (
            <Entry
              key={`${data['Firma 1']}-${data['Straße']}`}
              name={data['Firma 1']}
              street={data['Straße']}
              zip={data['PLZ']}
              place={data['Ort']}
              tel={data['Telefon']}
              web={data['Internet']}
              email={data['Email-Adresse']}
              distance={data['distance']}
              support={data['Technikschulung']}
              pfb={data['Pelletfachbetrieb']}
              fpk={data['Fachbetrieb Pelletkaminofen']}
              tags={[
                extractTag(data, 'Pelletöfen'),
                extractTag(data, 'Scheitholztechnik'),
                extractTag(data, 'Hackschnitzeltechnik'),
              ].filter(c => c !== null)}
            />
          );
        })}
      </React.Fragment>
    );
  };
  render() {
    const { companies, filter } = this.state;
    return (
      <Box>
        <ButtonContainer>
          <Button
            variant={!filter ? 'contained' : 'outlined'}
            size="s"
            onClick={() => {
              this.setState({
                filter: null,
              });
            }}
          >
            Alle anzeigen
          </Button>
          {buttonDefinitions.map(({ label, key }) => (
            <Button
              key={`filter-button-${key}`}
              variant={filter === key ? 'contained' : 'outlined'}
              onClick={() => {
                this.setState({
                  filter: key,
                });
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonContainer>
        <Wrap noPadding>
          <SearchInput
            placeholder="Postleitzahl eingeben"
            maxLength="5"
            onChange={this.handleSearch}
            type="number"
          />
        </Wrap>
        <Wrap bgcolor="grey.200">{companies && this.renderResults()}</Wrap>
      </Box>
    );
  }
}

export default PLZSearch;
